// ============================================
// PIPELINE REGISTRY - Metadata for all AI pipelines across stages
// ============================================
// Each pipeline defines its steps, prompt, config, and source location.
//
// Prompt storage strategy (Phase 5):
//   - localStorage: synchronous hot cache for reads (getPrompt is sync)
//   - IndexedDB (prompt_versions): authoritative version history, unlimited,
//     with performance telemetry per version
//   - One-time migration: localStorage → IndexedDB on first setPromptOverride call
//   - Writes go to BOTH stores; reads use localStorage (sync-first)

import {
    CONTACT_EXTRACTION_PROMPT,
    ASSESSOR_EXTRACTION_PROMPT,
    OCR_DOCUMENT_PROMPT,
    IMAGE_TEXT_TRIAGE_PROMPT,
    RFQ_SUMMARY_PROMPT,
    KEY_MOMENTS_PROMPT,
    BATCH_CLASSIFY_PROMPT,
    PHOTO_LABEL_PROMPT,
    PROJECT_SUMMARY_PROMPT,
    COST_RECOMPUTE_PROMPT,
    GOLD_STANDARD_EXTRACTION_PROMPT,
    PDF_COMPARISON_PROMPT,
} from './prompts';

const LS_KEY = 'qm_prompt_overrides';
const LS_HISTORY_KEY = 'qm_prompt_history';
const LS_MIGRATED_KEY = 'qm_prompt_migrated_to_idb';
// B-001 close (2026-04-25): parallel map of per-prompt fingerprints so we can
// detect when a user's localStorage override was authored against a stale
// default. Shape: { [promptId]: { defaultAtSet, acknowledgedDefault } }.
// Stored separately from the override text so the existing override shape
// stays flat and no migration is needed for pre-fingerprinting overrides.
const LS_FINGERPRINT_KEY = 'qm_prompt_override_fingerprints';

// ─── Persistence failure telemetry (Workstream I1) ──────────────────────
// Peter 2026-04-24: "Edits should all save properly and any edit conflicts
// and API database communication issues should be highly considered to make
// perfect and rock solid." A bare catch{} on a QuotaExceededError eats the
// user's prompt edit with zero feedback. We now:
//   1. Detect quota exhaustion specifically (err.name === 'QuotaExceededError'
//      or err.code === 22 for older WebKit).
//   2. Attempt recovery in tiers: trim-largest → prune-oldest-history → bail.
//   3. Emit a window event ('qm-prompt-save-failed') so App.jsx can toast.
//   4. Record the last failure so UI surfaces can render a sticky banner
//      via isPromptSaveFailed().
//
// Kill switch: localStorage['qm_disable_prompt_quota_guard'] === '1' restores
// the legacy silent-swallow behaviour (defensive rollback if the new logic
// misbehaves in production).
let _lastPromptSaveFailure = null; // { reason, key, size, at }

function _isQuotaError(err) {
    if (!err) return false;
    if (err.name === 'QuotaExceededError') return true;
    if (err.code === 22) return true; // legacy WebKit
    if (err.code === 1014 && err.name === 'NS_ERROR_DOM_QUOTA_REACHED') return true; // Firefox
    const msg = err.message || String(err);
    return /quota|exceeded|QUOTA_EXCEEDED/i.test(msg);
}

function _emitSaveFailed(detail) {
    _lastPromptSaveFailure = { ...detail, at: Date.now() };
    try {
        if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('qm-prompt-save-failed', { detail }));
        }
    } catch { /* non-fatal */ }
}

/**
 * Returns the most recent prompt-save failure (or null). Call-sites can
 * render a banner until the user dismisses or a later save succeeds.
 */
export function isPromptSaveFailed() {
    return _lastPromptSaveFailure;
}

/** Clear the recorded failure (e.g. after user dismisses the banner). */
export function clearPromptSaveFailed() {
    _lastPromptSaveFailure = null;
}

function _killSwitchDisabled() {
    try {
        return typeof localStorage !== 'undefined'
            && localStorage.getItem('qm_disable_prompt_quota_guard') === '1';
    } catch { return false; }
}

// ─── localStorage (sync hot cache) ─────────────────────────────────────

function loadOverrides() {
    try { return JSON.parse(localStorage.getItem(LS_KEY) || '{}'); } catch { return {}; }
}

/**
 * Persist prompt overrides to localStorage with quota-aware recovery.
 *
 * Return: { ok: true } on success, { ok: false, reason } on definitive failure.
 * On failure: emits 'qm-prompt-save-failed' and records telemetry.
 *
 * Recovery ladder (only when qm_disable_prompt_quota_guard is not set):
 *   1. Trim the largest prompt body in `overrides` to half its length
 *      (as a signal-preserving partial-save).
 *   2. Prune the oldest history entry across all promptIds (shrinks the
 *      sibling key `qm_prompt_history` so the overrides payload fits).
 *   3. Bail loud — emit event, return { ok: false }.
 */
function saveOverrides(overrides) {
    if (_killSwitchDisabled()) {
        try { localStorage.setItem(LS_KEY, JSON.stringify(overrides)); return { ok: true }; }
        catch { return { ok: false, reason: 'legacy_catch' }; }
    }

    const payload = JSON.stringify(overrides);
    try {
        localStorage.setItem(LS_KEY, payload);
        return { ok: true };
    } catch (err) {
        if (!_isQuotaError(err)) {
            _emitSaveFailed({ reason: 'unknown_error', attempted_key: LS_KEY, payload_size: payload.length, message: err?.message || String(err) });
            return { ok: false, reason: 'unknown_error', error: err };
        }
        // Quota exceeded: Tier 1 — trim the largest value to half.
        try {
            const trimmed = { ...overrides };
            let largestKey = null;
            let largestLen = 0;
            for (const [k, v] of Object.entries(trimmed)) {
                const len = typeof v === 'string' ? v.length : 0;
                if (len > largestLen) { largestLen = len; largestKey = k; }
            }
            if (largestKey && largestLen > 200) {
                trimmed[largestKey] = trimmed[largestKey].slice(0, Math.floor(largestLen / 2));
                localStorage.setItem(LS_KEY, JSON.stringify(trimmed));
                _emitSaveFailed({
                    reason: 'quota_exceeded_partial_save',
                    attempted_key: LS_KEY,
                    payload_size: payload.length,
                    recovered_via: 'trim_largest',
                    trimmed_prompt: largestKey,
                });
                return { ok: false, reason: 'quota_exceeded_partial_save', trimmedPrompt: largestKey };
            }
        } catch { /* fall through to Tier 2 */ }

        // Tier 2 — prune the oldest history entry, then retry.
        try {
            const history = loadHistory();
            let oldestPromptId = null;
            let oldestStamp = Infinity;
            for (const [pid, versions] of Object.entries(history)) {
                if (!Array.isArray(versions) || !versions.length) continue;
                const last = versions[versions.length - 1];
                const t = last?.savedAt ? new Date(last.savedAt).getTime() : 0;
                if (t < oldestStamp) { oldestStamp = t; oldestPromptId = pid; }
            }
            if (oldestPromptId && history[oldestPromptId]?.length) {
                history[oldestPromptId].pop();
                if (!history[oldestPromptId].length) delete history[oldestPromptId];
                try { localStorage.setItem(LS_HISTORY_KEY, JSON.stringify(history)); } catch { /* history write itself can fail; proceed to retry regardless */ }
                try {
                    localStorage.setItem(LS_KEY, payload);
                    return { ok: true, reason: 'recovered_via_history_prune' };
                } catch { /* fall through to bail */ }
            }
        } catch { /* fall through to bail */ }

        // Tier 3 — bail loud.
        _emitSaveFailed({
            reason: 'quota_exceeded',
            attempted_key: LS_KEY,
            payload_size: payload.length,
        });
        return { ok: false, reason: 'quota_exceeded' };
    }
}

function loadHistory() {
    try { return JSON.parse(localStorage.getItem(LS_HISTORY_KEY) || '{}'); } catch { return {}; }
}

/**
 * Save prompt history to localStorage with quota-aware recovery.
 * Same ladder as saveOverrides, but trims its OWN oldest entries first
 * (history is the bigger spender; trimming it is almost always safe
 * because the authoritative record lives in IndexedDB prompt_versions).
 */
function saveHistory(history) {
    if (_killSwitchDisabled()) {
        try { localStorage.setItem(LS_HISTORY_KEY, JSON.stringify(history)); return { ok: true }; }
        catch { return { ok: false, reason: 'legacy_catch' }; }
    }

    const payload = JSON.stringify(history);
    try {
        localStorage.setItem(LS_HISTORY_KEY, payload);
        return { ok: true };
    } catch (err) {
        if (!_isQuotaError(err)) {
            _emitSaveFailed({ reason: 'unknown_error', attempted_key: LS_HISTORY_KEY, payload_size: payload.length, message: err?.message || String(err) });
            return { ok: false, reason: 'unknown_error', error: err };
        }
        // Quota: trim each promptId's history to the newest 3 entries and retry.
        try {
            const trimmed = {};
            for (const [pid, versions] of Object.entries(history)) {
                if (Array.isArray(versions)) trimmed[pid] = versions.slice(0, 3);
            }
            localStorage.setItem(LS_HISTORY_KEY, JSON.stringify(trimmed));
            _emitSaveFailed({
                reason: 'quota_exceeded_partial_save',
                attempted_key: LS_HISTORY_KEY,
                payload_size: payload.length,
                recovered_via: 'history_truncate_to_3',
            });
            return { ok: false, reason: 'quota_exceeded_partial_save' };
        } catch { /* fall through */ }

        _emitSaveFailed({
            reason: 'quota_exceeded',
            attempted_key: LS_HISTORY_KEY,
            payload_size: payload.length,
        });
        return { ok: false, reason: 'quota_exceeded' };
    }
}

// ─── IndexedDB persistence (async, fire-and-forget from sync callers) ───

let _migrationRun = false;

/**
 * One-time migration: copy existing localStorage prompt history to IndexedDB.
 * Runs at most once per session. Does NOT delete localStorage keys - they
 * remain as the sync hot cache.
 */
async function migrateToIndexedDB() {
    if (_migrationRun) return;
    _migrationRun = true;

    try {
        // Skip if already migrated in a previous session
        if (localStorage.getItem(LS_MIGRATED_KEY) === 'true') return;

        const { DataService } = await import('../services/data-service.js');
        const history = loadHistory();

        for (const [promptId, versions] of Object.entries(history)) {
            for (let i = 0; i < versions.length; i++) {
                const entry = versions[i];
                await DataService.promptVersions.save({
                    promptId,
                    version: versions.length - i, // Oldest = 1, newest = N
                    text: entry.text || entry,
                    savedAt: entry.savedAt || null,
                    changeNote: entry.changeNote || null,
                    created_at: entry.savedAt || new Date().toISOString(),
                    source: 'migration',
                });
            }
        }

        localStorage.setItem(LS_MIGRATED_KEY, 'true');
        console.log('[PipelineRegistry] Migrated prompt history from localStorage to IndexedDB');
    } catch (e) {
        console.warn('[PipelineRegistry] Migration to IndexedDB failed (non-fatal):', e.message);
        _migrationRun = false; // Allow retry on next call
    }
}

/**
 * Persist a prompt version to IndexedDB (fire-and-forget).
 * Called from setPromptOverride - does not block the caller.
 *
 * Workstream I3 (2026-04-24): the `DataService.promptVersions.save` call
 * below already dual-writes to Postgres via the proxy in data-service.js
 * (see createProxy WRITE_METHODS + DataServiceAPI.promptVersions.save).
 * So a single save() attempts IDB first, then the API. If the API write
 * throws an unreachable-class error the proxy re-throws — we catch here
 * and emit `qm-prompt-backend-sync-failed` so the UI can banner that
 * sync is degraded WITHOUT breaking the local save.
 */
function persistVersionToIDB(promptId, text, changeNote) {
    // Fire-and-forget - don't await, don't block the sync caller
    (async () => {
        let idbOk = false;
        try {
            await migrateToIndexedDB();
            const { DataService } = await import('../services/data-service.js');
            const latest = await DataService.promptVersions.getLatest(promptId);
            const nextVersion = (latest?.version || 0) + 1;

            await DataService.promptVersions.save({
                promptId,
                version: nextVersion,
                text,
                changeNote: changeNote || null,
                created_at: new Date().toISOString(),
                source: 'user',
                performance: {
                    callCount: 0,
                    avgInputTokens: 0,
                    avgOutputTokens: 0,
                    avgLatencyMs: 0,
                    successRate: 0,
                    validationPassRate: 0,
                },
            });
            idbOk = true;
        } catch (e) {
            // DataService's proxy throws on backend failure (for writes) AFTER
            // having also tried Dexie. We distinguish "the API layer failed
            // but local Dexie succeeded" from "both failed" by re-attempting
            // a direct Dexie-only save as a belt-and-braces.
            //
            // I-001 close (2026-04-25): classify by HTTP status code first
            // (data-service-api stamps `err.status`), fall through to the
            // message regex only when status is absent. 4xx = schema/payload
            // bug — local fallback wouldn't fix it; emit a DIFFERENT event
            // (`qm-prompt-schema-error`) so the UI can banner "your save was
            // rejected by the backend" instead of pretending sync is just
            // temporarily degraded. 5xx / network = transient, worth the
            // local-only fallback so the edit isn't lost.
            const msg = e?.message || String(e);
            const status = typeof e?.status === 'number' ? e.status : null;
            const isSchemaError = status !== null && status >= 400 && status < 500 && status !== 401 && status !== 408 && status !== 429;
            const isTransientStatus = status !== null && (status >= 500 || status === 408 || status === 429);
            const matchesUnreachableMsg = /fetch|network|timeout|ECONN|ETIMEDOUT|ENOTFOUND|abort/i.test(msg);
            const isBackendIssue = isTransientStatus || (status === null && matchesUnreachableMsg);

            if (isSchemaError) {
                // Local fallback won't rescue a 4xx — the payload is bad. Tell
                // the UI so the editor can show a rejected-save banner. Do NOT
                // fall back to Dexie-only: sync is NOT degraded, the save
                // itself is malformed and must be corrected at the source.
                try {
                    if (typeof window !== 'undefined') {
                        window.dispatchEvent(new CustomEvent('qm-prompt-schema-error', {
                            detail: { promptId, status, message: msg, at: Date.now() },
                        }));
                    }
                } catch { /* non-fatal */ }
                console.warn(`[PipelineRegistry] Prompt save rejected by backend (${status}): ${msg}`);
            } else if (isBackendIssue) {
                try {
                    if (typeof window !== 'undefined') {
                        window.dispatchEvent(new CustomEvent('qm-prompt-backend-sync-failed', {
                            detail: { promptId, message: msg, at: Date.now() },
                        }));
                    }
                } catch { /* non-fatal */ }
                // Best-effort local-only save so the edit isn't lost.
                try {
                    const dbMod = await import('../services/db.js');
                    const db = dbMod.default;
                    if (db?.prompt_versions) {
                        // Derive nextVersion from Dexie directly (the proxy read may have failed).
                        const existing = await db.prompt_versions.where('promptId').equals(promptId).sortBy('version');
                        const nextVersion = ((existing[existing.length - 1]?.version) || 0) + 1;
                        await db.prompt_versions.add({
                            promptId,
                            version: nextVersion,
                            text,
                            changeNote: changeNote || null,
                            created_at: new Date().toISOString(),
                            source: 'user-local-fallback',
                            performance: {
                                callCount: 0, avgInputTokens: 0, avgOutputTokens: 0,
                                avgLatencyMs: 0, successRate: 0, validationPassRate: 0,
                            },
                        });
                        idbOk = true;
                    }
                } catch (fallbackErr) {
                    console.warn('[PipelineRegistry] Local fallback save also failed:', fallbackErr?.message);
                }
            } else {
                console.warn('[PipelineRegistry] Failed to persist version to IndexedDB:', msg);
            }
        }
        // Signal degraded-but-not-broken when IDB also failed — the localStorage
        // hot cache is the only surviving copy. The editor banner can warn.
        if (!idbOk) {
            try {
                if (typeof window !== 'undefined') {
                    window.dispatchEvent(new CustomEvent('qm-prompt-idb-sync-failed', {
                        detail: { promptId, at: Date.now() },
                    }));
                }
            } catch { /* non-fatal */ }
        }
    })();
}

// ─── Public API (synchronous - backward compatible) ─────────────────────

// Get prompt text - returns override if set, otherwise default
export function getPrompt(promptId) {
    const overrides = loadOverrides();
    return overrides[promptId] || PIPELINE_PROMPTS[promptId]?.default || '';
}

/**
 * Set prompt override.
 *
 * Contract (Workstream I1 2026-04-24):
 *   Returns { ok: true } on success, { ok: false, reason } on save failure.
 *   Callers that pre-date this change continue to work — they just ignore
 *   the return value. The function still swallows the failure silently at
 *   the function boundary (no throw); UI surfaces can either check the
 *   return value OR listen for the 'qm-prompt-save-failed' event.
 *
 * Revert-on-failure: if the primary saveOverrides fails definitively
 * (not a partial recovery), we roll back the history entry we added so
 * the history doesn't show a "new version" that was never actually saved.
 */
export function setPromptOverride(promptId, text, changeNote) {
    const prevOverrides = loadOverrides();
    const prevHistory = loadHistory();

    // Build next history with the existing text snapshotted.
    const nextHistory = { ...prevHistory };
    if (!nextHistory[promptId]) nextHistory[promptId] = [];
    nextHistory[promptId] = [
        {
            text: prevOverrides[promptId] || PIPELINE_PROMPTS[promptId]?.default || '',
            savedAt: new Date().toISOString(),
            changeNote: changeNote || null,
        },
        ...nextHistory[promptId],
    ];
    // Keep last 10 versions in localStorage (unlimited in IndexedDB)
    nextHistory[promptId] = nextHistory[promptId].slice(0, 10);
    const historyResult = saveHistory(nextHistory);

    const nextOverrides = { ...prevOverrides, [promptId]: text };
    const overridesResult = saveOverrides(nextOverrides);

    // If the overrides write definitively failed (no partial-recovery flag),
    // roll back the history so the UI doesn't show a phantom version entry.
    const overridesFailedHard = !overridesResult?.ok
        && overridesResult?.reason !== 'quota_exceeded_partial_save'
        && overridesResult?.reason !== 'recovered_via_history_prune';
    if (overridesFailedHard) {
        // Best effort revert — we may now ALSO be in a quota-bust state.
        try { localStorage.setItem(LS_HISTORY_KEY, JSON.stringify(prevHistory)); } catch { /* non-fatal */ }
        return { ok: false, reason: overridesResult?.reason || 'save_failed' };
    }

    // B-001: stash the default's fingerprint alongside the override so a
    // future default change raises a drift banner in the Prompt Editor.
    _recordPromptFingerprint(promptId);

    // Async: persist to IndexedDB (+ backend dual-write via proxy) for
    // unlimited history + telemetry + cross-device survivability.
    persistVersionToIDB(promptId, text, changeNote);

    // If history itself failed but overrides went through, that's still a
    // successful edit from the user's PoV — just with degraded history.
    if (!historyResult?.ok && historyResult?.reason !== 'quota_exceeded_partial_save') {
        return { ok: true, warning: 'history_save_degraded' };
    }
    return { ok: true };
}

// Reset prompt to default
export function resetPrompt(promptId) {
    const overrides = loadOverrides();
    delete overrides[promptId];
    saveOverrides(overrides);
    // Clear the paired fingerprint so the next setPromptOverride records fresh.
    _clearPromptFingerprint(promptId);
}

// Check if prompt has been overridden
export function isPromptOverridden(promptId) {
    const overrides = loadOverrides();
    return !!overrides[promptId];
}

// Get prompt history (sync - from localStorage, limited to 10)
export function getPromptHistory(promptId) {
    const history = loadHistory();
    return history[promptId] || [];
}

// ─── Prompt-version drift detection (B-001) ──────────────────────────────
// Each default prompt has a deterministic fingerprint. When a user edits
// (setPromptOverride) we stash the fingerprint of the default THAT OVERRIDE
// WAS BASED ON. On subsequent reads, if the default's current fingerprint
// differs, the override was authored against a stale default (e.g. the
// default gained `{TENANT_BLOCK}` after the override was saved).
// `getPromptDrift(promptId)` returns a signal the Prompt Editor can render
// as a banner; `acknowledgePromptDrift(promptId)` dismisses it without
// resetting the override ("I know, I want to keep my custom").
//
// djb2 hash — short, deterministic, no crypto dep. Collision-resistance
// isn't critical: we only compare two hashes of two prompt texts, and a
// collision merely means the banner doesn't fire for a real change — the
// user still sees their override and can Reset-to-default manually.

function _hashPrompt(text) {
    let h = 5381;
    const s = String(text || '');
    for (let i = 0; i < s.length; i++) {
        h = ((h << 5) + h + s.charCodeAt(i)) | 0;
    }
    return (h >>> 0).toString(16);
}

function loadFingerprints() {
    try { return JSON.parse(localStorage.getItem(LS_FINGERPRINT_KEY) || '{}'); } catch { return {}; }
}

function saveFingerprints(fps) {
    try { localStorage.setItem(LS_FINGERPRINT_KEY, JSON.stringify(fps)); } catch { /* non-fatal */ }
}

function _recordPromptFingerprint(promptId) {
    const def = PIPELINE_PROMPTS[promptId]?.default || '';
    const fp = _hashPrompt(def);
    const fps = loadFingerprints();
    fps[promptId] = {
        defaultAtSet: fp,
        acknowledgedDefault: fp, // initial save = implicit acknowledgement of current default
    };
    saveFingerprints(fps);
}

/**
 * Detect drift between the user's override and the current default prompt.
 *
 * Returns `null` when there is no drift to surface (no override, or the
 * user has already acknowledged the current default). Returns an object
 * otherwise:
 *
 *   { kind: 'legacy', overrideFingerprint: null, defaultFingerprint: <hex> }
 *     A pre-B-001 override with no recorded fingerprint. Banner copy:
 *     "Your saved prompt pre-dates version tracking — defaults may have
 *     changed since. Reset, or Keep custom to silence."
 *
 *   { kind: 'drift', overrideFingerprint: <hex>, defaultFingerprint: <hex> }
 *     The override was authored against a default that has since changed.
 *     Banner copy: "The default prompt has been updated since you last
 *     edited. Your override may be missing placeholders like {TENANT_BLOCK}."
 */
export function getPromptDrift(promptId) {
    const overrides = loadOverrides();
    if (!overrides[promptId]) return null;
    const defaultFp = _hashPrompt(PIPELINE_PROMPTS[promptId]?.default || '');
    const record = loadFingerprints()[promptId];
    if (!record) {
        return { kind: 'legacy', overrideFingerprint: null, defaultFingerprint: defaultFp };
    }
    if (record.acknowledgedDefault === defaultFp) return null;
    return {
        kind: 'drift',
        overrideFingerprint: record.defaultAtSet,
        defaultFingerprint: defaultFp,
    };
}

/** User dismisses the drift banner without resetting. */
export function acknowledgePromptDrift(promptId) {
    const fps = loadFingerprints();
    const defaultFp = _hashPrompt(PIPELINE_PROMPTS[promptId]?.default || '');
    fps[promptId] = {
        ...(fps[promptId] || { defaultAtSet: defaultFp }),
        acknowledgedDefault: defaultFp,
    };
    saveFingerprints(fps);
}

/** Clear any fingerprint record for a prompt (used by resetPrompt). */
function _clearPromptFingerprint(promptId) {
    const fps = loadFingerprints();
    if (fps[promptId]) {
        delete fps[promptId];
        saveFingerprints(fps);
    }
}

/**
 * Record performance telemetry for the current prompt version.
 * Called by PipelineMonitor.success() after each AI call.
 *
 * @param {string} promptId - e.g. 'analysis', 'report-synthesis'
 * @param {Object} metrics - { inputTokens, outputTokens, latencyMs, success, validationPassed }
 */
export async function recordPromptPerformance(promptId, metrics) {
    try {
        const { DataService } = await import('../services/data-service.js');
        const latest = await DataService.promptVersions.getLatest(promptId);
        if (!latest || !latest.performance) return;

        const perf = latest.performance;
        const n = perf.callCount || 0;

        // Running averages
        perf.callCount = n + 1;
        perf.avgInputTokens = Math.round(((perf.avgInputTokens * n) + (metrics.inputTokens || 0)) / (n + 1));
        perf.avgOutputTokens = Math.round(((perf.avgOutputTokens * n) + (metrics.outputTokens || 0)) / (n + 1));
        perf.avgLatencyMs = Math.round(((perf.avgLatencyMs * n) + (metrics.latencyMs || 0)) / (n + 1));

        // Success rate as running fraction
        const successes = Math.round(perf.successRate * n) + (metrics.success ? 1 : 0);
        perf.successRate = Math.round((successes / (n + 1)) * 100) / 100;

        // Validation pass rate
        if (metrics.validationPassed !== undefined) {
            const passes = Math.round((perf.validationPassRate || 0) * n) + (metrics.validationPassed ? 1 : 0);
            perf.validationPassRate = Math.round((passes / (n + 1)) * 100) / 100;
        }

        // Update in place - uses update() not save() to avoid add() ConstraintError
        await DataService.promptVersions.update(latest.id, { performance: perf });
    } catch (e) {
        // Non-fatal - telemetry should never crash the app
        console.warn('[PipelineRegistry] Performance telemetry write failed:', e.message);
    }
}

// ============================================
// PROMPT DEFINITIONS (defaults from source code)
// ============================================
// AI-2 audit 2026-04-21 (registry-side reconciliation): each entry's
// `default` field is a REFERENCE to the prompt constant imported from
// src/config/prompts.js. This registry does NOT copy prompt text — it
// just wires the imported const through. When the SEC specialist adds
// `<document>...</document>` XML fencing + system directives to the
// prompt bodies in prompts.js, those changes propagate automatically
// here. No reconciliation needed. `strongRecommendation` on the
// model-selector side is also an orthogonal concern that lives in
// model-selector.js, not here. If a future refactor ever copies prompt
// text into this file, re-audit to preserve SEC's fencing.
export const PIPELINE_PROMPTS = {
    'contact-extraction': {
        name: 'Contact Extraction (RFQ)',
        default: CONTACT_EXTRACTION_PROMPT,
        stage: 1,
        sourceFile: 'src/config/prompts.js',
        sourceLines: '172-239',
        placeholders: ['${documentLabel}', '${referenceInventory}', '${content}'],
        description: 'Extracts client contact, address, and project details from RFQ documents'
    },
    'assessor-extraction': {
        name: 'Inspector Extraction (Notes)',
        default: ASSESSOR_EXTRACTION_PROMPT,
        stage: 1,
        sourceFile: 'src/config/prompts.js',
        sourceLines: '241-262',
        placeholders: ['${documentLabel}', '${content}'],
        description: 'Extracts inspector name and visit date from site assessment notes'
    },
    'ocr-document': {
        name: 'OCR Document Text',
        default: OCR_DOCUMENT_PROMPT,
        stage: 1,
        sourceFile: 'src/config/prompts.js',
        sourceLines: '264-284',
        placeholders: [],
        description: 'Extracts text from photos of documents using AI vision OCR'
    },
    'image-text-triage': {
        name: 'Image Text Check',
        default: IMAGE_TEXT_TRIAGE_PROMPT,
        stage: 1,
        sourceFile: 'src/config/prompts.js',
        sourceLines: 'IMAGE_TEXT_TRIAGE_PROMPT',
        placeholders: ['{IMAGE_COUNT}'],
        description: 'Sorts up to 12 small copies of imported images into document, site photo with writing, or plain photo, so only images with writing are sent for OCR.'
    },
    'key-moments': {
        name: 'Video Key Moments',
        default: KEY_MOMENTS_PROMPT,
        stage: 1,
        sourceFile: 'src/config/prompts.js',
        sourceLines: '466-486',
        placeholders: ['${videoDuration}', '${maxFrames}', '${transcript}'],
        description: 'Identifies key moments in video transcripts for frame extraction'
    },
    'cost-recompute': {
        name: 'Cost Breakdown Recompute',
        default: COST_RECOMPUTE_PROMPT,
        stage: 4,
        sourceFile: 'src/config/prompts.js',
        sourceLines: '110-150',
        placeholders: ['{LOCALITY_BLOCK}', '{PROJECT_CONTEXT}', '{TITLE}', '{DESCRIPTION}', '{INCLUSIONS}', '{PRICING_REFERENCE}', '{CHOSEN_APPROACH}', '{WORK_METHOD}'],
        description: 'Regenerates the cost breakdown for one scope item when its inclusions change via the QM Assistant.',
    },
    'rfq-summary': {
        name: 'RFQ Summary',
        default: RFQ_SUMMARY_PROMPT,
        stage: 1,
        sourceFile: 'src/config/prompts.js',
        sourceLines: '332-352',
        placeholders: ['${documentLabel}', '${content}'],
        description: 'Generates structured summary of RFQ document content'
    },
    'project-summary': {
        name: 'Project Summary',
        default: PROJECT_SUMMARY_PROMPT,
        stage: 1,
        sourceFile: 'src/config/prompts.js',
        sourceLines: '366-464',
        placeholders: ['{CONTEXT}'],
        description: 'Pre-analysis best-guess pass - runs before Risk Analysis for the Summary tab of the Stage 1 Job cockpit: a confidence-scored interpretation of the RFQ + photos + notes'
    },
    'batch-classify': {
        name: 'Batch Document Classification',
        default: BATCH_CLASSIFY_PROMPT,
        stage: 1,
        sourceFile: 'src/config/prompts.js',
        sourceLines: '488-514',
        placeholders: ['${documentList}'],
        description: 'Classifies all imported documents in one AI call - identifies RFQ, inspector notes, reports, plans'
    },
    'photo-label': {
        name: 'Photo Labeling',
        default: PHOTO_LABEL_PROMPT,
        stage: 1,
        sourceFile: 'src/config/prompts.js',
        sourceLines: '516-537',
        placeholders: ['${projectContext}', '${photoList}'],
        description: 'Labels imported photos using project context from RFQ and inspector notes'
    },
    'ai-assistant': {
        name: 'AI Assistant (Global)',
        default: null,
        stage: 4,
        sourceFile: 'src/pages/stages/QuoteMakerAssistantPanel.jsx',
        sourceLines: '740-900',
        placeholders: [],
        description: 'Context-aware AI assistant for editing scope items, pricing, inclusions, and CRM operations - runs from any page via the global panel'
    },
    'photo-assign': {
        name: 'Photo Auto-Assignment',
        default: null,
        stage: 4,
        sourceFile: 'src/pages/stages/Stage4ScopeBuilder.jsx',
        sourceLines: '417-520',
        placeholders: [],
        description: 'Matches photos to scope items and selects hero image using AI vision'
    },
    'gold-standard-extraction': {
        name: 'Gold Standard Extraction',
        default: GOLD_STANDARD_EXTRACTION_PROMPT,
        stage: 5,
        sourceFile: 'src/pages/QuickEditPage.tsx',
        sourceLines: 'buildGoldStandardExtractionPrompt',
        placeholders: ['{BUSINESS_QUOTE}', '${sourceFileName}', '${projectType}', '${address}', '${rfqContext}', '${pdfContent}'],
        description: 'Structured extraction of a completed builder-sent PDF into RJ Bird gold-standard training data: objective, quote title, client-facing descriptions, inclusions, subheadings, notes, HBCF/admin presentation, validity, and inc-GST item amounts. Does not invent internal cost breakdowns.',
    },
    'pdf-comparison': {
        name: 'PDF Comparison (AI vs Sent)',
        default: PDF_COMPARISON_PROMPT,
        stage: 5,
        sourceFile: 'src/pages/stages/QuoteMakerAssistantPanel.jsx',
        sourceLines: 'pdfComparison',
        placeholders: [],
        description: 'Diffs the quote on screen against the builder\u2019s final sent PDF and offers the differences as edits to apply.',
    },
};

// ============================================
// PIPELINE DEFINITIONS (metadata per pipeline)
// ============================================
const DEFAULT_AI_PIPELINE_CONFIG = {
    temperature: { value: 0.2, min: 0, max: 1, step: 0.05, label: 'Temperature' },
};

function registeredAiPipeline({ id, name, stage, promptId = null, steps = [], config = DEFAULT_AI_PIPELINE_CONFIG, inputShape = 'Runtime context assembled by caller', outputShape = 'AI response consumed by caller', description }) {
    return { id, name, stage, promptId, steps, config, inputShape, outputShape, description };
}

export const PIPELINES = {
    // Stage 1
    'contact-extraction': {
        id: 'contact-extraction',
        name: 'Contact Extraction',
        stage: 1,
        promptId: 'contact-extraction',
        steps: ['Document', 'Parse Text', 'AI Extract JSON', 'Validate', 'Auto-Fill Form'],
        config: {
            temperature: { value: 0.1, min: 0, max: 1, step: 0.05, label: 'Temperature' },
            forceFill: { value: true, type: 'boolean', label: 'Force Fill Empty Fields' }
        },
        inputShape: 'Document text (PDF/EML/DOCX content)',
        outputShape: '{ clientName, clientCompany, clientEmail, clientPhone, siteContactName, siteContactPhone, address, strataPlan, orderNumber, visitDate, projectDescription }',
        description: 'Extracts client contact details and project info from uploaded RFQ documents'
    },
    'assessor-extraction': registeredAiPipeline({
        id: 'assessor-extraction',
        name: 'Inspector Extraction',
        stage: 1,
        promptId: 'assessor-extraction',
        steps: ['Inspector Notes', 'AI Extract Inspector Fields', 'Validate', 'Auto-Fill Form'],
        inputShape: 'Inspector/site assessment notes text',
        outputShape: '{ assessor, visitDate, siteNotesSummary }',
        description: 'Extracts inspector name, visit date, and note metadata from assessor notes during Stage 1 intake.'
    }),
    'classification-verifier': registeredAiPipeline({
        id: 'classification-verifier',
        name: 'Classification Verifier',
        stage: 1,
        promptId: null,
        steps: ['Deterministic Classification', 'Owner/Staff Context', 'AI Verify', 'Apply Override or Keep Result'],
        inputShape: 'Current Stage 1 form fields + classified documents',
        outputShape: '{ clientIsCorrect, override, reason }',
        description: 'Checks whether document-derived client/contact classification is plausible before it feeds downstream project state.'
    }),
    'photo-ocr': {
        id: 'photo-ocr',
        name: 'Photo OCR',
        stage: 1,
        promptId: 'ocr-document',
        steps: ['Image', 'Compress (long side 2048px)', 'AI Vision OCR', 'Store as Document'],
        config: {
            maxWidth: { value: 1600, min: 800, max: 3200, step: 200, label: 'Max Width (px)' },
            maxHeight: { value: 1200, min: 600, max: 2400, step: 200, label: 'Max Height (px)' },
            temperature: { value: 0.1, min: 0, max: 1, step: 0.05, label: 'Temperature' },
            maxTokens: { value: 4096, min: 1024, max: 16384, step: 1024, label: 'Max Tokens' }
        },
        inputShape: 'Image data URL (JPEG, compressed)',
        outputShape: 'Extracted text string',
        description: 'Extracts text from document photos (a single photo of a document) using AI vision.'
    },
    'image-text-triage': registeredAiPipeline({
        id: 'image-text-triage',
        name: 'Image Text Check',
        stage: 1,
        promptId: 'image-text-triage',
        steps: ['Small copies (512px)', 'AI sort, up to 12 per call', 'Parse JSON (unreadable = photo)', 'Send documents to Photo OCR'],
        inputShape: 'Up to 12 JPEG thumbnails, long side 512px',
        outputShape: '{ images: [{ index, kind: "document" | "both" | "photo" }] }',
        description: 'Decides from the image content which imported images carry writing. Every image that opens is checked; the file name plays no part.'
    }),
    // 2026-05-02 Peter: split out from photo-ocr so the live pipeline monitor
    // shows image-based PDF page OCR as its own pipeline. Same model bucket
    // (pdf-ocr task in TASK_DEFAULTS) but distinct telemetry. Stage 1 image-
    // based PDF imports use this id; the legacy 'photo-ocr' pipelineId on the
    // same call site was misleading because the input is a PDF page, not a
    // photo of a document.
    'pdf-ocr': {
        id: 'pdf-ocr',
        name: 'PDF Page OCR',
        stage: 1,
        promptId: 'ocr-document',
        steps: ['PDF Pages → JPEG render', 'AI Vision OCR', 'Combine into single text'],
        config: {
            scale: { value: 1.5, min: 1.0, max: 3.0, step: 0.1, label: 'Render scale' },
            quality: { value: 0.85, min: 0.5, max: 1.0, step: 0.05, label: 'JPEG quality' },
            temperature: { value: 0.1, min: 0, max: 1, step: 0.05, label: 'Temperature' },
        },
        inputShape: 'Image-based PDF (no extractable text)',
        outputShape: 'Combined extracted text from all rendered pages',
        description: 'OCR pass for image-based PDFs where pdfjs text extraction returned no usable text. Renders pages to JPEG then runs the AI vision model on each.'
    },
    // 2026-05-02 Peter: NEW. Walks the pdfjs operator stream on every page,
    // extracts embedded XObject images (e.g. site photos inside a dilapidation
    // report), and adds them to the project's photo set. Closes the gap where
    // text-extractable reports with embedded photos lost all visual content.
    'pdf-embed-extract': {
        id: 'pdf-embed-extract',
        name: 'PDF Embedded Image Extract',
        stage: 1,
        promptId: null,           // not an AI call - pure pdfjs walk
        steps: ['Walk pdfjs operator list', 'Resolve XObject images', 'Filter by min dimension', 'Encode to JPEG dataURL'],
        config: {
            quality: { value: 0.85, min: 0.5, max: 1.0, step: 0.05, label: 'JPEG quality' },
        },
        inputShape: 'PDF file (typically a report or spec)',
        outputShape: 'Array of { page, imageId, dataUrl, width, height }',
        description: 'Pure pdfjs operation (no AI call). Extracts embedded images from a PDF for downstream feeding into the photo pipeline. Surfaces embedded site photos that text extraction misses.'
    },
    'photo-description': registeredAiPipeline({
        id: 'photo-description',
        name: 'Photo Description',
        stage: 1,
        promptId: null,
        steps: ['Photo Batch', 'Inject Project Context', 'AI Vision Description', 'Attach Field Notes'],
        config: {
            batchSize: { value: 4, min: 1, max: 12, step: 1, label: 'Photos per batch' },
            temperature: { value: 0.4, min: 0, max: 1, step: 0.05, label: 'Temperature' },
            maxTokens: { value: 2048, min: 512, max: 8192, step: 512, label: 'Max Output Tokens' },
        },
        inputShape: 'Site photos + RFQ/inspector/project context',
        outputShape: '[{ index, description }]',
        description: 'Writes concise photo field notes used by later scope, WMS, and quote photo assignment passes.'
    }),
    'plan-analysis': registeredAiPipeline({
        id: 'plan-analysis',
        name: 'Plan / Drawing Analysis',
        stage: 1,
        promptId: null,
        steps: ['Plan Image/PDF Page', 'AI Vision Read', 'Extract Dimensions/Notes', 'Attach to Documents'],
        inputShape: 'Plan, drawing, or report page rendered for vision analysis',
        outputShape: 'Structured plan observations and extracted text',
        description: 'Analyses architectural, engineering, or marked-up plan pages when PDF text extraction is not enough.'
    }),
    'video-processing': {
        id: 'video-processing',
        name: 'Video Processing',
        stage: 1,
        promptId: 'key-moments',
        steps: ['Video', 'Extract Audio (8kHz)', 'Whisper Transcribe', 'AI Key Moments', 'Extract Frames'],
        config: {
            targetRate: { value: 8000, min: 4000, max: 16000, step: 1000, label: 'Audio Sample Rate (Hz)' },
            maxDuration: { value: 600, min: 60, max: 1800, step: 60, label: 'Max Duration (sec)' },
            processTimeout: { value: 120000, min: 30000, max: 300000, step: 30000, label: 'Process Timeout (ms)' },
            temperature: { value: 0.3, min: 0, max: 1, step: 0.05, label: 'Temperature (Key Moments)' }
        },
        inputShape: 'Video file (MP4/WebM/MOV)',
        outputShape: '{ transcript, keyMoments[], frames[] }',
        description: 'Full video pipeline: audio extraction, transcription, key moment detection, frame capture'
    },
    'rfq-analysis': {
        id: 'rfq-analysis',
        name: 'RFQ Analysis',
        stage: 1,
        promptId: 'rfq-summary',
        steps: ['Document Content', 'AI Summarise', 'Display Summary'],
        config: {
            temperature: { value: 0.2, min: 0, max: 1, step: 0.05, label: 'Temperature' },
        },
        inputShape: 'RFQ document text',
        outputShape: 'Structured summary (Request, Scope, Requirements, Notes)',
        description: 'Generates actionable summary of RFQ content for the builder'
    },
    'project-summary': {
        id: 'project-summary',
        name: 'Project Summary',
        stage: 1,
        promptId: 'project-summary',
        steps: ['Build Context', 'Inject Documents/Photos/Notes', 'AI Best-Guess', 'Parse JSON', 'Show in the Summary tab'],
        config: {
            temperature: { value: 0.3, min: 0, max: 1, step: 0.05, label: 'Temperature' },
            maxDocsChars: { value: 12000, min: 4000, max: 24000, step: 2000, label: 'Max Doc Chars' },
            maxPerDoc: { value: 4000, min: 1000, max: 8000, step: 1000, label: 'Max Per Document' }
        },
        inputShape: 'formData + documents + photos + notes + video transcripts + preprocessed files',
        outputShape: '{ project_type, project_type_confidence, scope_objective, estimated_value_range, complexity_factors, urgency_signal, key_facts[], data_quality, missing_information[], suggested_clarifying_questions[], best_guess_summary }',
        description: 'Pre-analysis best-guess interpretation of the RFQ. Runs after Stage 1 (Data Entry), before Stage 2 (Risk Analysis). Always commits to a guess with explicit confidence percentages.'
    },
    // NOTE: 'client-search' was removed from the registry on 2026-04-08.
    // It is NOT an AI pipeline - it is a local Dexie/IndexedDB fuzzy string
    // search (ClientDatabaseService.search). PipelineMonitor only tracks
    // AI calls, so listing it here caused it to appear as "registered but
    // never run" in the Developer Tools monitor, confusing the user.
    'batch-classify': {
        id: 'batch-classify',
        name: 'Batch Document Classification',
        stage: 1,
        promptId: 'batch-classify',
        steps: ['Extract All Text', 'Build Summaries', 'AI Classify Batch', 'Apply Types'],
        config: {
            temperature: { value: 0.1, min: 0, max: 1, step: 0.05, label: 'Temperature' },
            summaryLength: { value: 500, min: 200, max: 1000, step: 100, label: 'Per-Doc Summary (chars)' }
        },
        inputShape: 'Array of { position, format, email from/subject, content_preview } (never the file name, CN5)',
        outputShape: '{ rfq_present, classifications: [{ index, classification, confidence, reason }] }',
        description: 'Single AI call to classify all uploaded documents by role. Runtime uses the routed model ceiling so large imports cannot silently lose classifications.'
    },
    'photo-label': {
        id: 'photo-label',
        name: 'Photo Labeling',
        stage: 1,
        promptId: 'photo-label',
        steps: ['Collect Photos', 'Build Project Context', 'AI Label', 'Update Photos'],
        config: {
            temperature: { value: 0.3, min: 0, max: 1, step: 0.05, label: 'Temperature' },
            maxTokens: { value: 2048, min: 512, max: 8192, step: 512, label: 'Max Tokens' }
        },
        inputShape: 'Photo positions and images + project context from RFQ/notes (never the file name, CN5)',
        outputShape: '[{ index, label }]',
        description: 'Labels photos using project context from classified documents'
    },
    // Stage 4 (Scope Builder) - global assistant anchored here
    'ai-assistant': {
        id: 'ai-assistant',
        name: 'AI Assistant',
        stage: 4,
        promptId: 'ai-assistant',
        steps: ['User Message', 'Build Context', 'Inject Pricing', 'Inject Learnings', 'AI Response', 'Parse Actions'],
        config: {
            // 0.3 → 0.2 (2026-07-11) in lockstep with assistant-agent.js
            // DEFAULT_TEMPERATURE. Display-value here; the live request value
            // is assistant-agent's constant. Note Sonnet 5 (the normal route's
            // default model) does not support temperature at all — the gateway
            // strips it; this bites on the Haiku/Opus routes.
            temperature: { value: 0.2, min: 0, max: 1, step: 0.05, label: 'Temperature' }
        },
        inputShape: 'User message + scope context + photo context + pricing + learnings',
        outputShape: '{ message, actions[] }',
        description: 'Context-aware AI assistant for scope editing, pricing, and inclusions'
    },
    'inclusions-generator': registeredAiPipeline({
        id: 'inclusions-generator',
        name: 'Inclusions Generator',
        stage: 4,
        promptId: null,
        steps: ['Scope Item', 'Assistant Context', 'AI Draft Inclusions', 'Apply Suggested Inclusions'],
        inputShape: 'Selected scope item + current project context',
        outputShape: 'Draft inclusion list for the selected scope item',
        description: 'Assistant tool that generates detailed inclusions for a scope item when the user asks for them.'
    }),
    'title-suggester': registeredAiPipeline({
        id: 'title-suggester',
        name: 'Title Suggester',
        stage: 4,
        promptId: null,
        steps: ['Scope/Quote Context', 'AI Suggest Title', 'Return Options'],
        inputShape: 'Current scope, project address, and quote context',
        outputShape: 'Suggested quote or scope title',
        description: 'Assistant tool that suggests concise RJ Bird-style titles from the current scope context.'
    }),
    'photo-auto-assign': {
        id: 'photo-auto-assign',
        name: 'Photo Auto-Assignment',
        stage: 4,
        promptId: 'photo-assign',
        steps: ['Scope Items + Photos', 'AI Vision Match', 'Apply Assignments', 'Select Hero'],
        config: {
            maxPhotos: { value: 10, min: 1, max: 20, step: 1, label: 'Max Photos' },
            temperature: { value: 0.3, min: 0, max: 1, step: 0.05, label: 'Temperature' }
        },
        inputShape: 'Scope summary + photo descriptions + images',
        outputShape: '{ assignments[], heroPhotoIndex, reasoning }',
        description: 'Matches photos to scope items and selects hero image'
    },
    'cost-recompute': {
        id: 'cost-recompute',
        name: 'Cost Breakdown Recompute',
        stage: 4,
        promptId: 'cost-recompute',
        steps: ['Item + Inclusions', 'Inject Pricing', 'AI Cost Estimate', 'Parse JSON', 'Apply to Item'],
        config: {
            temperature: { value: 0.2, min: 0, max: 1, step: 0.05, label: 'Temperature' }
        },
        inputShape: 'Single scope item (title, description, updated inclusions) + project context + pricing reference',
        outputShape: 'cost_estimate JSON: { materials, labour, equipment, other, direct_cost_total, contingency_pct, overhead_pct, total_cost }',
        description: 'Auto-fired by the QM Assistant when a scope items inclusions change. Regenerates the per-item cost breakdown at the routed model ceiling so detailed internal working stays complete without disrupting the rest of the quote.'
    },
    'market-price-research': {
        id: 'market-price-research',
        name: 'Market Price Research',
        stage: 3,
        promptId: null,
        steps: ['Unmatched Materials/Equipment', 'OpenRouter Web Search', 'Citation Validation', 'Deterministic 30% Uplift', 'Re-run Pricing Authority'],
        config: {
            temperature: { value: 0.1, min: 0, max: 1, step: 0.05, label: 'Temperature' },
        },
        inputShape: 'Unresolved material/equipment lines after Francis rate-card matching',
        outputShape: '{ prices[{gap_id, supplier, source_url, observed_price_aud, tax_basis, unit, checked_at}], unresolved_gap_ids[] }',
        description: 'Bounded supplier-price research for Francis rate-card gaps. URL citations are mandatory; deterministic code removes GST, applies the 30% uplift, and never auto-writes results into the tenant rate card.',
    },
    // 2026-05-02 (OW-102): kpi-scoring + learning-capture registry entries
    // deleted. Both had promptId:null + zero pipelineId references in src/.
    // KPI calc is deterministic local code (src/utils/helpers.js, no AI). The
    // learning capture went with the learning loop (old pipeline removal, D4).
    'email-generation': registeredAiPipeline({
        id: 'email-generation',
        name: 'Email Generation',
        stage: 5,
        promptId: null,
        steps: ['Quote Context', 'AI Draft Email', 'User Review', 'Send via Email Modal'],
        inputShape: 'Final quote + client/contact context',
        outputShape: 'Draft email subject/body',
        description: 'Drafts the client email that accompanies the finished quote. Sending still requires explicit user action.'
    }),
    'pdf-comparison': registeredAiPipeline({
        id: 'pdf-comparison',
        name: 'PDF Comparison (AI vs Sent)',
        stage: 5,
        promptId: 'pdf-comparison',
        steps: ['AI Quote', 'Sent PDF Text', 'AI Compare', 'Return Learnings Candidates'],
        config: {
            temperature: { value: 0.2, min: 0, max: 1, step: 0.05, label: 'Temperature' },
        },
        inputShape: 'Generated QuoteMaker quote + uploaded final sent quote PDF text',
        outputShape: '{ differences[], learnings_suggested[] }',
        description: 'Compares QuoteMaker output to the builder-sent PDF so the system can learn style, scope, and pricing differences.'
    }),
    'gold-standard-extraction': registeredAiPipeline({
        id: 'gold-standard-extraction',
        name: 'Gold Standard Extraction',
        stage: 5,
        promptId: 'gold-standard-extraction',
        steps: ['Completed Quote PDF', 'AI Extract Structured Quote', 'Validate', 'Save Gold Standard'],
        config: {
            temperature: { value: 0.2, min: 0, max: 1, step: 0.05, label: 'Temperature' },
        },
        inputShape: 'Completed RJ Bird quote PDF text + optional RFQ context',
        outputShape: '{ quoteTitle, objective, scopeItems[], totals, terms, adminItems }',
        description: 'Extracts builder-approved sent quotes into structured RJ Bird gold-standard training data.'
    }),

    // Support AI that can run outside one exact quote stage
    'assistant-voice-transcription': registeredAiPipeline({
        id: 'assistant-voice-transcription',
        name: 'Assistant Voice Transcription',
        stage: 'support',
        promptId: null,
        steps: ['Audio Blob', 'Multimodal Transcription Model', 'Append Text to Assistant Prompt'],
        inputShape: 'Recorded assistant audio data URL',
        outputShape: 'Transcript text',
        description: 'Transcribes QuoteMaker Assistant voice input through the AI gateway.'
    })
};

// Get pipelines for a specific integer stage. Decimal stages (e.g. 1.75 for
// the diagnostic orchestrator) get rounded UP so they appear in the next
// integer tab - so photo-forensic / evidence-canonicaliser at stage 1.75
// render under the "Stage 2 - Risk Report" tab alongside the risk analysis
// they feed into. Integer stages match exactly.
export function getPipelinesForStage(stageNum) {
    return Object.values(PIPELINES).filter(p => {
        if (typeof stageNum !== 'number') return p.stage === stageNum;
        if (typeof p.stage !== 'number') return false;
        if (p.stage === stageNum) return true;
        // Round fractional stages up so 1.75 maps to stage 2.
        return Number.isInteger(p.stage) ? false : Math.ceil(p.stage) === stageNum;
    });
}

// Get all prompts for the library tab
export function getAllPrompts() {
    return Object.entries(PIPELINE_PROMPTS).map(([id, prompt]) => ({
        id,
        ...prompt,
        currentText: getPrompt(id),
        isOverridden: isPromptOverridden(id)
    }));
}

// ============================================
// CONFIG OVERRIDES - editable pipeline config values
// ============================================
const LS_CONFIG_KEY = 'qm_config_overrides';

function loadConfigOverrides() {
    try { return JSON.parse(localStorage.getItem(LS_CONFIG_KEY) || '{}'); } catch { return {}; }
}

function saveConfigOverrides(overrides) {
    localStorage.setItem(LS_CONFIG_KEY, JSON.stringify(overrides));
}

// Get a config value - returns override if set, otherwise default
export function getConfigValue(pipelineId, configKey) {
    const overrides = loadConfigOverrides();
    const override = overrides[pipelineId]?.[configKey];
    if (override !== undefined) return override;
    return PIPELINES[pipelineId]?.config?.[configKey]?.value;
}

// Set a config override
export function setConfigOverride(pipelineId, configKey, value) {
    const overrides = loadConfigOverrides();
    if (!overrides[pipelineId]) overrides[pipelineId] = {};
    overrides[pipelineId][configKey] = value;
    saveConfigOverrides(overrides);
}

// Reset a config value to default
export function resetConfigValue(pipelineId, configKey) {
    const overrides = loadConfigOverrides();
    if (overrides[pipelineId]) {
        delete overrides[pipelineId][configKey];
        if (Object.keys(overrides[pipelineId]).length === 0) delete overrides[pipelineId];
        saveConfigOverrides(overrides);
    }
}

// Check if config has been overridden
export function isConfigOverridden(pipelineId, configKey) {
    const overrides = loadConfigOverrides();
    return overrides[pipelineId]?.[configKey] !== undefined;
}
